import Vue from "vue"
import customerHubPreferences from "./customerHubPreferences"
import customerHubApi from "@/api/customerHubApi"
import healthAdapter from "./customerHubHealthAdapter"
import { buildHubActivities, hubDay, hubStatistics, hubPeriod } from "./customerHubActivityData"
import RangeCache from "./customerHubRangeCache"
import { trainingComparisonRange } from "./customerHubTrainingProgress"
import { store } from "@/store/stores"

const storageKey = owner => `roody:customer-hub:activity-decisions:${owner}`
const emptyComparison = () => ({ range: null, activities: [], checkins: [], coverage: null, error: "" })

function storedDecisions(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  return Object.fromEntries(Object.entries(value).filter(([id, decision]) => id && decision && typeof decision === "object" &&
    ((decision.action === "merge" && typeof decision.targetId === "string" && decision.targetId && decision.targetId !== id) ||
      (decision.action === "separate" && Array.isArray(decision.targetIds) && decision.targetIds.every(target => typeof target === "string" && target))))
    .map(([id, decision]) => [id, decision.action === "merge" ? { action: "merge", targetId: decision.targetId } : { action: "separate", targetIds: [...new Set(decision.targetIds)] }]))
}

class CustomerHubActivityService {
  constructor() {
    this.state = Vue.observable({ owner: "", busy: false, refreshing: false, error: "", decisionError: "", activities: [], checkins: [], coverage: null, comparison: emptyComparison(), decisions: {}, requestKey: "" })
    this.cache = new RangeCache(8, 12000)
    this.version = 0
    this.token = ""
    this.pending = null
    this.healthScope = ""
    this.entrySnapshot = null
    this.comparisonSnapshot = null
  }

  start() {
    if (this.unwatch) return
    this.unwatch = store.watch(() => `${customerHubPreferences.isEnabled()}:${customerHubPreferences.state.userId}:${customerHubPreferences.state.revision}`, () => {
      const identity = customerHubPreferences.identity()
      if (!customerHubPreferences.isEnabled() || identity.userId !== this.state.owner || identity.token !== this.token) this.reset()
    })
    this.unwatchHealth = store.watch(() => healthAdapter.scope(), () => {
      const { userId, token } = customerHubPreferences.identity(), version = this.version
      this.healthScope = ""
      this.entrySnapshot = null; this.comparisonSnapshot = null
      // Let the adapter's permission watcher clear its prior snapshot first.
      Promise.resolve().then(() => {
        if (this.current(userId, token, version) && !this.state.busy && this.state.coverage) this.requestHealth(this.state.coverage, userId, token, version)
      })
    })
  }

  stop() { this.unwatch?.(); this.unwatchHealth?.(); this.unwatch = null; this.unwatchHealth = null; this.reset() }
  invalidate() {
    this.version++; this.pending = null
    this.cache.clear(); this.healthScope = ""
    this.state.busy = false; this.state.refreshing = false
    this.state.comparison = emptyComparison()
    this.entrySnapshot = null; this.comparisonSnapshot = null
    healthAdapter.invalidateActivities(); healthAdapter.resetActivities()
  }

  ensureOwner() {
    const { userId, token } = customerHubPreferences.identity()
    if (!customerHubPreferences.isEnabled() || !userId || !token) { this.reset(); return null }
    if (this.state.owner !== userId || this.token !== token) {
      this.reset()
      this.state.owner = userId
      this.token = token
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey(userId)) || "{}")
        this.state.decisions = storedDecisions(stored)
      } catch (_) { this.state.decisions = {} }
    }
    return { userId, token }
  }

  reset() {
    this.cache.clear()
    this.version++
    this.token = ""
    this.pending = null
    this.healthScope = ""
    this.entrySnapshot = null; this.comparisonSnapshot = null
    Object.assign(this.state, { owner: "", busy: false, refreshing: false, error: "", decisionError: "", activities: [], checkins: [], coverage: null, comparison: emptyComparison(), decisions: {}, requestKey: "" })
    healthAdapter.reset()
  }

  current(owner, token, version) {
    const identity = customerHubPreferences.identity()
    return customerHubPreferences.isEnabled() && this.state.owner === owner && owner === identity.userId && token === identity.token && version === this.version
  }

  load(params = {}, force = false) {
    const identity = this.ensureOwner()
    if (!identity) return Promise.resolve()
    const { userId, token } = identity
    // The same week/month has one key, regardless of the selected day.
    const { compare = false, ...query } = params
    const period = query.period && query.period !== "all" ? hubPeriod(query.period, query.anchor) : null
    const requestParams = period ? { ...query, anchor: period.from } : { ...query }
    const comparisonRange = compare && period ? trainingComparisonRange(period.period, period.from) : null
    const key = JSON.stringify([Object.keys(requestParams).sort().map(key => [key, requestParams[key]]), comparisonRange])
    if (!force && this.pending?.key === key && this.pending.userId === userId && this.pending.token === token) return this.pending.promise
    const cached = this.cache.get(key)
    const version = ++this.version
    this.state.busy = !cached
    this.state.refreshing = Boolean(cached)
    this.state.error = ""
    this.state.requestKey = key
    // Never show the previous period's totals while the new period is loading.
    this.state.activities = cached?.value.activities || []
    this.state.checkins = cached?.value.checkins || []
    this.state.coverage = cached?.value.coverage || null
    this.state.comparison = cached?.value.comparison || emptyComparison()
    this.healthScope = ""
    healthAdapter.resetActivities()
    if (force) healthAdapter.invalidateActivities()
    if (cached) {
      this.requestHealth(cached.value.coverage, userId, token, version)
      const ttl = cached.value.coverage.to < hubDay() ? 300000 : 0
      if (!force && Date.now() - cached.at < ttl) { this.pending = null; this.state.refreshing = false; return Promise.resolve() }
    }
    const request = { key, userId, token, version, promise: null }
    this.pending = request
    request.promise = (async () => {
      try {
        const [response, previous] = await Promise.all([
          customerHubApi.getActivity(requestParams, token),
          comparisonRange ? customerHubApi.getActivity({ from: comparisonRange.from, to: comparisonRange.to }, token).catch(() => null) : null,
        ])
        if (!this.current(userId, token, version)) return
        if (response?.success !== true || String(response.userId) !== userId || !Array.isArray(response.activities) || !Array.isArray(response.checkins) || !response.coverage || typeof response.coverage !== "object" || Array.isArray(response.coverage)) throw new Error("Ungültige Aktivitätsantwort")
        this.state.activities = response.activities
        this.state.checkins = response.checkins
        this.state.coverage = response.coverage
        const validPrevious = previous?.success === true && String(previous.userId) === userId && Array.isArray(previous.activities) && Array.isArray(previous.checkins) && previous.coverage && typeof previous.coverage === "object" && !Array.isArray(previous.coverage)
        this.state.comparison = comparisonRange ? { range: comparisonRange, activities: validPrevious ? previous.activities : [], checkins: validPrevious ? previous.checkins : [], coverage: validPrevious ? previous.coverage : null, error: validPrevious ? "" : "Der Vergleichszeitraum konnte nicht geladen werden." } : emptyComparison()
        const comparison = this.state.comparison
        if (!comparison.error) this.cache.put(key, { activities: response.activities, checkins: response.checkins, coverage: response.coverage, comparison }, response.activities.length + response.checkins.length + comparison.activities.length + comparison.checkins.length)
        // Publish the statistics only once the server and device have both settled.
        if (this.current(userId, token, version)) await this.requestHealth(response.coverage, userId, token, version)
      } catch (error) {
        if (this.current(userId, token, version)) {
          this.state.error = cached ? "Neue Aktivitäten konnten nicht geladen werden. Du siehst den zuletzt geladenen Stand." : "Deine Aktivitäten konnten nicht geladen werden. Bitte versuche es erneut."
          if ([401, 403].includes(error?.response?.status)) customerHubPreferences.resolve(userId, token, [])
        }
      }
    })().finally(() => {
      if (this.pending !== request) return
      this.pending = null
      if (this.current(userId, token, version)) { this.state.busy = false; this.state.refreshing = false }
      else this.reset()
    })
    return request.promise
  }

  requestHealth(coverage, userId, token, version) {
    if (!this.current(userId, token, version)) return
    // Health may predate the first Roody workout. The adapter independently
    // bounds the readable device history and reports that limit to the user.
    const deviceFrom = healthAdapter.historyFrom()
    const from = coverage.allHistory ? [coverage.from || deviceFrom, deviceFrom].sort()[0] : this.state.comparison.coverage ? this.state.comparison.range.from : coverage.from || hubDay()
    const to = coverage.to || hubDay()
    this.healthScope = healthAdapter.scope()
    try { return Promise.resolve(healthAdapter.loadRange(from, to)).catch(() => {}) }
    catch (_) { this.healthScope = "" }
  }

  entries() {
    if (!customerHubPreferences.isEnabled() || this.state.owner !== customerHubPreferences.identity().userId || this.token !== customerHubPreferences.identity().token) return []
    const scope = healthAdapter.scope()
    const workouts = scope && scope === this.healthScope ? healthAdapter.state.workouts : null
    return this.activitySnapshot("entrySnapshot", this.state.activities, workouts)
  }

  activitySnapshot(key, activities, workouts) {
    const previous = this[key], decisions = this.state.decisions
    if (previous && previous.activities === activities && previous.workouts === workouts && previous.decisions === decisions) return previous.value
    const value = buildHubActivities(activities, workouts || [], decisions)
    this[key] = { activities, workouts, decisions, value }
    return value
  }

  statistics(range) {
    const { userId, token } = customerHubPreferences.identity()
    if (!customerHubPreferences.isEnabled() || this.state.owner !== userId || this.token !== token) return hubStatistics([], [])
    const selectedRange = range || this.state.coverage || {}
    return hubStatistics(this.entries(), this.state.checkins, selectedRange.period === "all" || selectedRange.allHistory ? {} : selectedRange)
  }

  comparisonStatistics() {
    const { userId, token } = customerHubPreferences.identity(), comparison = this.state.comparison
    if (!customerHubPreferences.isEnabled() || this.state.owner !== userId || this.token !== token || !comparison.coverage) return null
    const scope = healthAdapter.scope()
    const entries = this.activitySnapshot("comparisonSnapshot", comparison.activities, scope && scope === this.healthScope ? healthAdapter.state.workouts : null)
    return hubStatistics(entries, comparison.checkins, comparison.range)
  }

  undoMerge(activity) {
    if (!this.ensureOwner()) return
    const current = this.entries().find(item => item.id === activity?.id && item.resolution === "confirmed")
    if (!current) return
    const refs = new Set(current.sourceIds), decisions = { ...this.state.decisions }
    Object.entries(this.state.decisions).forEach(([id, decision]) => {
      if (decision?.action === "merge" && (refs.has(id) || refs.has(decision.targetId))) delete decisions[id]
    })
    this.saveDecisions(decisions)
  }

  decide(id, action, targetId = "") {
    if (!this.ensureOwner()) return
    const item = this.entries().find(row => row.id === id)
    if (action !== "clear" && (!item || !["merge", "separate"].includes(action))) return
    if (action === "merge" && !item.overlapCandidates.some(other => other.id === targetId)) return
    const decisions = { ...this.state.decisions }
    if (action === "clear") delete decisions[id]
    else decisions[id] = action === "merge" ? { action, targetId } : { action, targetIds: item.overlapCandidates.map(other => other.id) }
    this.saveDecisions(decisions)
  }

  saveDecisions(decisions) {
    this.state.decisions = decisions
    this.state.decisionError = ""
    try { localStorage.setItem(storageKey(this.state.owner), JSON.stringify(decisions)) }
    catch (_) { this.state.decisionError = "Die Zuordnung gilt für diese Sitzung. Sie konnte auf diesem Gerät nicht gespeichert werden." }
  }
}

export default new CustomerHubActivityService()
